import { useNavigate, useParams } from "react-router-dom";
import PageStructure, { PageContent, PageContentHeader, PageContentInner, Pane, PaneContent } from "./PageStructure.component";
import { invalidateJoinPathOptions, invalidateReportBuilderData, MetricType, ReportBuilderDimensionJoinTree, ReportBuilderMeasureORM, useMeasure, useMeasureJoinPathOptions, useMeasures } from "@models/reportBuilder";
import { useCallback, useEffect, useMemo, useState } from "react";
import { DataWhitelist, PipelineNode, PipelineNodeMeasureJoinTree } from "@models/pipelineNode";
import { Badge, Form, Offcanvas, Tab, Tabs } from "react-bootstrap";
import Dropdown, { MultiDropdown } from "@components/form/Dropdown.component";
import PipelineNodeName, { PipelineNodeRelationshipName } from "@components/pipelineNodes/PipelineNodeName.component";
import PipelineNodeJoinTreeLabel from "@components/pipelineNodes/mapping/PipelineNodeJoinTreeLabel.component";
import { useImmer } from "use-immer";
import DataWhitelistForm from "@components/pipelineNodes/PipelineNodeDataWhitelist.component";
import { useRouteBlocker } from "@services/routing.service";
import SaveButton from "@components/button/SaveButton.component";
import { getPromptAnswer, requireConfirmation } from "@services/alert/alert.service";
import toast from "@services/toast.service";
import { getErrorMessage } from "@services/errors.service";
import Warning from "@components/statusIndicators/Warning.component";
import PipelineNodeSelector from "@components/pipelineNodes/PipelineNodeSelector.component";
import PipelineNodeColumnSelector from "@components/pipelineNodes/PipelineNodeColumnSelector.component";
import { useMissionControlDataFlowData, usePipelineNodes, usePipelineNodeRelationships, usePipelineNode } from "@stores/data.store";
import { DashboardIcon } from "@components/pipelineNodes/PipelineNodeIcon.component";
import { getAllRelatedNodeIds } from "@components/missionControl/dataflow/utils";
import { getGroupValueForNodeType } from "@services/modeling.service";
import { getAggregationOptionsForFieldType } from "@services/metrics.service";
import { DraftOnly, ReadOnly } from "@components/project/DraftModeRequired.component";
import { propTypes } from "react-bootstrap/esm/Image";
import LoadingCard from "@components/card/LoadingCard.component";

const joinPathLength = (node: PipelineNodeMeasureJoinTree): number => {
    let rv = 1;
    if (node.downstream) {
        node.downstream.forEach(d => {
            rv += joinPathLength(d);
        });
    }
    return rv;
}

const FORMAT_OPTIONS = [{
    label: 'Percent',
    value: 'PERCENT',
}, {
    label: 'Financial',
    value: 'FINANCIAL',
}, {
    label: 'Numeric',
    value: 'NUMERIC',
}, {
    label: 'Multiplier',
    value: 'MULTIPLIER',
}]

const METRIC_TYPE_OPTIONS = [{
    label: 'Basic',
    value: 'BASIC',
    description: 'A simple aggregation of values from a single entity.',
}, {
    label: 'Calculated',
    value: 'CALCULATED',
    description: 'A formula that uses other metrics to calculate a value.',
}]

interface JoinTreeWithSize {
    tree: PipelineNodeMeasureJoinTree;
    size: number;
}


const getRelationshipIds = (tree: any): string[] => {
    const ids: string[] = [];
    if (tree.relationship_id) {
        ids.push(tree.relationship_id);
    }
    for (const downstream of tree.downstream || []) {
        ids.push(...getRelationshipIds(downstream));
    }
    return ids;
};

// Custom Path Builder Component
interface CustomPathNode {
    nodeId: string;
    relationshipId?: string;
    children: CustomPathNode[];
}

// Custom Path Node Component
interface CustomPathNodeComponentProps {
    node: CustomPathNode;
    upstreamNode?: CustomPathNode;
    path: number[];
    level: number;
    customPath: CustomPathNode;
    onAddNode: (parentPath: number[]) => void;
    onUpdateNode: (path: number[], nodeId: string, relationshipId?: string) => void;
    onRemoveNode: (path: number[]) => void;
    readOnly?: boolean;
}

const CustomPathNodeComponent = ({ node, path, level, customPath, onAddNode, onUpdateNode, onRemoveNode, readOnly, upstreamNode }: CustomPathNodeComponentProps) => {
    const relationships = usePipelineNodeRelationships(upstreamNode?.nodeId || undefined);
    
    // Get all used relationship IDs from the entire path to prevent reuse
    const getUsedRelationshipIds = (customPath: CustomPathNode): string[] => {
        const usedIds: string[] = [];
        
        const traverse = (nodes: CustomPathNode[]) => {
            nodes.forEach(node => {
                if (node.relationshipId) {
                    usedIds.push(node.relationshipId);
                }
                if (node.children) {
                    traverse(node.children);
                }
            });
        };
        
        traverse([customPath]);
        return usedIds;
    };
    
    const usedRelationshipIds = getUsedRelationshipIds(customPath);
    
    const availableRelationships = useMemo(() => {
        if (!upstreamNode) {
            return [];
        }
        return relationships.data?.filter(rel => {
            if (rel.id == node.relationshipId) {
                return true;
            }

            if (usedRelationshipIds.includes(rel.id as string)) {
                return false;
            }
            return rel.child_node_id === upstreamNode.nodeId || rel.parent_node_id === upstreamNode.nodeId;
        }) || [];
    }, [relationships.dataUpdatedAt, upstreamNode, usedRelationshipIds]);
    
    const getConnectedNodeId = useCallback((rel: any) => {
        if (!upstreamNode) {
            return '';
        }
        return rel.child_node_id === upstreamNode.nodeId ? rel.parent_node_id : rel.child_node_id;
    }, [upstreamNode]);
    
    return (
        <div key={path.join('-')} style={{ marginLeft: level * 10 }}>
            <div className="d-flex align-items-center mb-2">
                <div className="flex-1 me-2">
                    {readOnly && <strong>
                        <i className="mdi mdi-table"></i>
                        <PipelineNodeName pipelineNodeId={node.nodeId} />
                    </strong>}
                    {!readOnly && <>
                        <select 
                            className="form-select form-select-sm"
                            value={node.relationshipId || ''}
                            onChange={(e) => {
                                const rel = availableRelationships.find(r => r.id === e.target.value);
                                if (rel) {
                                    onUpdateNode(path, getConnectedNodeId(rel), rel.id || undefined);
                                }
                            }}
                        >
                            <option value="">Select related entity...</option>
                            {availableRelationships.map(rel => {
                                const targetNodeId = getConnectedNodeId(rel);
                                return (
                                    <option key={rel.id || ''} value={rel.id || ''}>
                                        <PipelineNodeName pipelineNodeId={targetNodeId} /> (via <PipelineNodeRelationshipName relationshipId={rel.id || ''} />)
                                    </option>
                                );
                            })}
                        </select>
                        </>}
                </div>
                <div className="">
                    <button 
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => onAddNode(path)}
                        disabled={!readOnly && (!node.nodeId || !node.relationshipId)}
                        title="Add connection"
                    >
                        <i className="mdi mdi-plus-thick"></i>
                    </button>
                    {path.length > 0 && (
                        <button 
                            className="btn btn-sm btn-outline-danger ms-1"
                            onClick={() => onRemoveNode(path)}
                            title="Remove this connection"
                        >
                            <i className="mdi mdi-close-thick"></i>
                        </button>
                    )}
                </div>
            </div>
            
            
            {/* Show children if this node has a relationship selected */}
            {(node.relationshipId || readOnly) && node.children && node.children.length > 0 && (
                <div style={{ marginLeft: 20 }}>
                    {node.children.map((child, index) => (
                        <CustomPathNodeComponent 
                            key={index}
                            node={child} 
                            path={[...path, index]} 
                            level={level + 1}
                            customPath={customPath}
                            onAddNode={onAddNode}
                            onUpdateNode={onUpdateNode}
                            onRemoveNode={onRemoveNode}
                            upstreamNode={node}
                            
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

interface CustomPathBuilderProps {
    startNodeId: string;
    targetDimensionNodeId: string;
    dataWhitelist: DataWhitelist;
    onSave: (customPath: PipelineNodeMeasureJoinTree) => void;
    existingCustomPath?: PipelineNodeMeasureJoinTree;
}

const CustomPathBuilder = ({ startNodeId, targetDimensionNodeId, dataWhitelist, onSave, existingCustomPath }: CustomPathBuilderProps) => {
    const [customPath, setCustomPath] = useImmer<CustomPathNode>({
        nodeId: '',
        relationshipId: '',
        children: [],
    });
    const [isExpanded, setIsExpanded] = useState(false);
    
    // Get all required nodes that need to be included in the path
    const getRequiredNodes = () => {
        const requiredNodes = [targetDimensionNodeId];
        
        // Add nodes from data whitelist filters
        if (dataWhitelist.entries) {
            dataWhitelist.entries.forEach(entry => {
                if (entry.pipeline_node_id && !requiredNodes.includes(entry.pipeline_node_id)) {
                    requiredNodes.push(entry.pipeline_node_id);
                }
            });
        }
        
        return requiredNodes;
    };
    
    // Check if a node is included in the custom path
    const isNodeIncluded = (nodeId: string, nodes: CustomPathNode[]): boolean => {
        for (const node of nodes) {
            if (node.nodeId === nodeId) {
                return true;
            }
            if (node.children && isNodeIncluded(nodeId, node.children)) {
                return true;
            }
        }
        return false;
    };
    
    // Get validation status for each required node
    const getValidationStatus = () => {
        const requiredNodes = getRequiredNodes();
        return requiredNodes.map(nodeId => ({
            nodeId,
            isIncluded: isNodeIncluded(nodeId, [customPath]),
            nodeName: <PipelineNodeName pipelineNodeId={nodeId} />
        }));
    };
    
    const validationStatus = getValidationStatus();
    const allRequirementsMet = validationStatus.every(item => item.isIncluded);
    
    // Convert existing custom path to our format for pre-population
    const convertJoinTreeToCustomPath = (tree: PipelineNodeMeasureJoinTree): CustomPathNode => {
        if (!tree) {
            return {
                nodeId: '',
                relationshipId: '',
                children: [],
            }
        };
                
        return {
            nodeId: tree.node_id,
            relationshipId: tree.relationship_id,
            children: tree.downstream ? tree.downstream.map(convertJoinTreeToCustomPath) || [] : []
        };
    };
    
    // Convert our custom path back to join tree format
    const convertCustomPathToJoinTree = (node: CustomPathNode): PipelineNodeMeasureJoinTree => {
        console.log('converting node', node);
        return {
            node_id: node.nodeId,
            relationship_id: node.relationshipId,
            downstream: node.children.map(convertCustomPathToJoinTree),
        };
    };
    
    // Initialize with existing custom path if available
    useEffect(() => {
        if (existingCustomPath) {
            setCustomPath(convertJoinTreeToCustomPath(existingCustomPath));
            setIsExpanded(true);
        } else {
            setCustomPath({ nodeId: startNodeId, children: [] });
        }
    }, [existingCustomPath, startNodeId]);
    
    const addNode = (parentPath: number[]) => {
        setCustomPath(draft => {
            let current = draft;
            
            // Navigate to the parent location
            for (let i = 0; i < parentPath.length; i++) {
                current = current.children[parentPath[i]];
            }
            
            current.children.push({
                nodeId: '',
                children: [],
                relationshipId: '',
            });
            
        });
    };
    
    const updateNode = (path: number[], nodeId: string, relationshipId?: string) => {
        setCustomPath(prev => {
            let current = prev;
            
            for (let i = 0; i < path.length; i++) {
                current = current.children[path[i]];
            }
            console.log('Current is', current.nodeId, current.relationshipId, current.children);


            current.nodeId = nodeId;
            current.relationshipId = relationshipId;
            // Clear all downstream nodes when upstream selection changes
            current.children = [];
            
        });
    };
    
    const removeNode = (path: number[]) => {
        setCustomPath(prev => {
            let current = prev;
            
            for (let i = 0; i < path.length - 1; i++) {
                current = current.children[path[i]];
            }
            
            const lastIndex = path[path.length - 1];
            current.children.splice(lastIndex, 1);
            
            // return newPath;
        });
    };
    
    const handleSave = () => {
        const joinTree = convertCustomPathToJoinTree(customPath);
        if (joinTree) {
            console.log('Saving', joinTree, customPath);
            onSave(joinTree);
        }
    };
    
    
    
    return (
        <div className="border rounded p-3 mb-3">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h4 className="mb-0">Custom Join Path</h4>
                <div className="d-flex gap-2">
                    <button 
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() => setIsExpanded(!isExpanded)}
                    >
                        {isExpanded ? 'Collapse' : 'Expand'}
                    </button>
                    <button 
                        className="btn btn-sm btn-primary"
                        onClick={handleSave}
                        disabled={customPath.children.length === 0 || !customPath.nodeId || !allRequirementsMet}
                    >
                        Use Custom Path
                    </button>
                </div>
            </div>
            
            
            
            {isExpanded && (
                <div>
                    {/* Validation Status */}
                    <div className="mb-3">
                        <div className="text-muted small mb-2">
                            <strong>Requirements:</strong> Your custom path must include all of the following nodes:
                        </div>
                        <div className="list-group list-group-flush">
                            {validationStatus.map((item, index) => (
                                <div key={index} className="list-group-item d-flex align-items-center py-1">
                                    <div className="me-2">
                                        {item.isIncluded ? (
                                            <i className="mdi mdi-check-circle text-success"></i>
                                        ) : (
                                            <i className="mdi mdi-close-circle text-danger"></i>
                                        )}
                                    </div>
                                    <div className="flex-1">
                                        {item.nodeName}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                    <CustomPathNodeComponent 
                        key={0}
                        node={customPath} 
                        path={[]} 
                        level={0}
                        customPath={customPath}
                        onAddNode={addNode}
                        onUpdateNode={updateNode}
                        onRemoveNode={removeNode}
                        readOnly
                    />
                </div>
            )}
        </div>
    );
};

const MetricDetailsPage = () => {
    const { metricId } = useParams();

    const allMeasures = useMeasures();
    const joinPathOptions = useMeasureJoinPathOptions(metricId as string);

    const joinPathOptionsByPipelineNodeId = useMemo(() => {
        const result: {[key: string]: PipelineNodeMeasureJoinTree[]} = {};

        joinPathOptions.data?.forEach(jpo => {
            const withSizes = jpo.possible_paths.map(jp => {
                return {
                    tree: jp,
                    size: joinPathLength(jp),
                };
            });
            result[jpo.pipeline_node_id] = withSizes.sort((a, b) => {
                return a.size - b.size;
            }).map(jp => jp.tree) || [];
        });
        return result;
    }, [joinPathOptions.dataUpdatedAt]);


    const upstreamMeasureOptions = useMemo(() => {
        return (allMeasures.data?.filter(m => m.id != metricId) || []).map(m => {
            return {
                label: m.name,
                value: m.id as string,
                description: m.description,
            }
        });
    }, [metricId, allMeasures.dataUpdatedAt]);



    const [name, setName] = useState('');
    const [columnHeader, setColumnHeader] = useState('');
    const [description, setDescription] = useState('');
    const [isCalculation, setIsCalculation] = useState(false);

    const [parentMetricIds, setParentMetricIds] = useState<string[]>([]);

    const [formula, setFormula] = useState('');
    const [aggregator, setAggregator] = useState('');
    const [formatter, setFormatter] = useState('');
    const [metricType, setMetricType] = useState<MetricType | ''>('');
    const [dataWhitelist, setDataWhitelist] = useImmer<DataWhitelist>({
        entries: [],
        logic_gate: 'AND',
    });
    const [dimensionJoinTrees, setDimensionJoinTrees] = useImmer<ReportBuilderDimensionJoinTree[]>([]);
    const [pipelineNodeId, setPipelineNodeId] = useState('');
    const [fieldId, setFieldId] = useState('');
    const [activityPipelineNodeId, setActivityPipelineNodeId] = useState('');
    const [activityFieldId, setActivityFieldId] = useState('');
    const [activityAggregation, setActivityAggregation] = useState('');
    const [activityLookbackStart, setActivityLookbackStart] = useState<number | ''>('');
    const [activityLookbackEnd, setActivityLookbackEnd] = useState<number | ''>('');

    const measure = useMeasure(metricId as string);

    const graphData = useMissionControlDataFlowData();

    const nodeIdsForWhitelist = useMemo(() => {
        // Get all the pipeline node ids that are connected to the root node
        if (!graphData.data) {
            console.log('No graph data');
            return [];
        }

        const thisNode = graphData.data.nodes.find(n => n.id == 'PipelineNode:' + pipelineNodeId);

        if (!thisNode) {
            return;
        }
        const allRelatedNodeIds = getAllRelatedNodeIds(thisNode, graphData.data.nodes, graphData.data.edges, (nodeType: string) => {
            return getGroupValueForNodeType(nodeType) == 'DATA_MODELING';
        });

        return [pipelineNodeId].concat(allRelatedNodeIds);
    }, [graphData.dataUpdatedAt, pipelineNodeId]);

    const formulaVariables = useMemo(() => {
        return allMeasures.data?.filter(m => parentMetricIds.includes(m.id as string)).map(m => '"' + m.column_name + '"') || [];
    }, [parentMetricIds, allMeasures.dataUpdatedAt]);

    const disableDimension = useCallback((idx: number) => {
        setDimensionJoinTrees(draft => {
            draft[idx].ignore = true;
        });
        setPageDirty(true);
    }, []);

    const enableDimension = useCallback((idx: number) => {
        setDimensionJoinTrees(draft => {
            draft[idx].ignore = false;
        });
        setPageDirty(true);
    }, []);

    const saveMetric = useCallback(async () => {
        try {
            await ReportBuilderMeasureORM.save({
                id: metricId as string,
                name,
                column_name: columnHeader,
                description,
                is_calculation: isCalculation,
                parent_measure_ids: parentMetricIds,
                formula,
                aggregator,
                formatter,
                metric_type: metricType || undefined,
                data_whitelist: dataWhitelist,
                dimension_join_trees: dimensionJoinTrees,
                pipeline_node_id: pipelineNodeId,
                field_id: fieldId,
                activity_pipeline_node_id: activityPipelineNodeId || undefined,
                activity_field_id: activityFieldId || undefined,
                activity_aggregation: activityAggregation || undefined,
                activity_lookback_periods_ago_start: activityLookbackStart === '' ? undefined : activityLookbackStart,
                activity_lookback_periods_ago_end: activityLookbackEnd === '' ? undefined : activityLookbackEnd,
            });
            toast('success', 'Success', 'Metric saved');
            invalidateReportBuilderData();
            invalidateJoinPathOptions(metricId as string);
            setPageDirty(false);
        } catch (err) {
            toast('danger', 'Error', getErrorMessage(err));
        }
        
    }, [name, columnHeader, description, isCalculation, parentMetricIds, formula, aggregator, formatter, metricType, dataWhitelist, dimensionJoinTrees, pipelineNodeId, fieldId, activityPipelineNodeId, activityFieldId, activityAggregation, activityLookbackStart, activityLookbackEnd]);
    const { pageDirty, setPageDirty } = useRouteBlocker(saveMetric);

    const [activeTab, setActiveTab] = useState('basic');

    useEffect(() => {
        const allowedTabs = ['basic', 'source', 'dimensions', 'whitelist'];
        const params = new URLSearchParams(window.location.search);
        const tab = params.get('tab');
        if (tab && allowedTabs.includes(tab)) {
            setActiveTab(tab);
        }
    }, []);

    useEffect(() => {
        if (measure.data) {
            setName(measure.data.name);
            setColumnHeader(measure.data.column_name);
            setIsCalculation(!!measure.data.is_calculation);
            setFormula(measure.data.formula || '');
            setAggregator(measure.data.aggregator || '');
            setFormatter(measure.data.formatter || '');
            setMetricType(measure.data.metric_type || '');
            setDataWhitelist(measure.data.data_whitelist ? measure.data.data_whitelist : {entries: [], logic_gate: 'AND'});
            setDimensionJoinTrees(measure.data.dimension_join_trees || []);
            setPipelineNodeId(measure.data.pipeline_node_id || '');
            setFieldId(measure.data.field_id || '');
            setDescription(measure.data.description || '');
            setParentMetricIds(measure.data.parent_measure_ids || []);
            setActivityPipelineNodeId(measure.data.activity_pipeline_node_id || '');
            setActivityFieldId(measure.data.activity_field_id || '');
            setActivityAggregation(measure.data.activity_aggregation || '');
            setActivityLookbackStart(measure.data.activity_lookback_periods_ago_start ?? '');
            setActivityLookbackEnd(measure.data.activity_lookback_periods_ago_end ?? '');
        }
    }, [measure.dataUpdatedAt]);

    const [activeDimensionPipelineNodeId, setActiveDimensionPipelineNodeId] = useState('');

    const activeDimensionJoinTree = useMemo(() => {
        return dimensionJoinTrees.find(djt => djt.pipeline_node_id == activeDimensionPipelineNodeId);
    }, [activeDimensionPipelineNodeId, dimensionJoinTrees]);

    const onSelectActiveDimensionJoinPath = useCallback((pathIdx: number) => {
        setDimensionJoinTrees(draft => {
            draft.forEach(djt => {
                if (djt.pipeline_node_id == activeDimensionPipelineNodeId) {
                    djt.join_tree = joinPathOptionsByPipelineNodeId[activeDimensionPipelineNodeId]![pathIdx];
                }
            });
        });
        setActiveDimensionPipelineNodeId('');
        setPageDirty(true);
    }, [activeDimensionPipelineNodeId, dimensionJoinTrees, joinPathOptionsByPipelineNodeId]);

    const navigate = useNavigate();
    const copyMetric = useCallback(async () => {
        const newName = await getPromptAnswer('Enter a new name for the copied metric', 'Copy Metric', false, name + ' (Copy)');
        if (newName) {
            const result = await ReportBuilderMeasureORM.save({
                ...measure.data,
                id: null,
                name: newName,
                column_name: newName,
                description: 'Copy of ' + name,
            });
            invalidateReportBuilderData();
            navigate(`/metric/${result.id}`);
        }
    }, [name, measure.dataUpdatedAt])

    const deleteMetric = useCallback(async () => {
        const confirmed = await requireConfirmation('Are you sure you want to delete this metric?');
        if (confirmed) {
            await ReportBuilderMeasureORM.deleteById(metricId as string);
            invalidateReportBuilderData();
            navigate(`/`);
        }
    }, [metricId]);

    const pipelineNodes = usePipelineNodes();

    const sourceField = useMemo(() => {
        return pipelineNodes.data?.find(n => n.id == pipelineNodeId)?.fields.find(f => f.id == fieldId);
    }, [pipelineNodeId, fieldId, pipelineNodes.dataUpdatedAt]);

    // COPY PASTE ALERT from DataModelWizard
    const aggregationOptions = useMemo(() => {
        let fieldType: string;

        const selectedField = pipelineNodes.data?.find(n => n.id == pipelineNodeId)?.fields.find(f => f.id == fieldId);
        fieldType = selectedField?.type || 'STRING';
        
        return getAggregationOptionsForFieldType(fieldType);

    }, [fieldId, pipelineNodeId, pipelineNodes.dataUpdatedAt]);

    const activityAggregationOptions = useMemo(() => {
        let fieldType: string;

        const selectedField = pipelineNodes.data?.find(n => n.id == activityPipelineNodeId)?.fields.find(f => f.id == activityFieldId);
        fieldType = selectedField?.type || 'STRING';
        
        return getAggregationOptionsForFieldType(fieldType);

    }, [activityFieldId, activityPipelineNodeId, pipelineNodes.dataUpdatedAt]);

    const cohortAggregationOptions = useMemo(() => {
        return [
            {
                label: 'Count Still-Active',
                value: 'COUNT_NON_ZERO_COHORT_CONDITION',
                description: 'Counts entities that had activity in both the prior period and this period, e.g. for retention.',
            },
            {
                label: 'Count Deactivated',
                value: 'COUNT_ZERO_COHORT_CONDITION',
                description: 'Counts entities that had activity in the prior period but not in this period, e.g. for churn.',
            },
            {
                label: 'Count Reactivated',
                value: 'COUNT_REACTIVATED_CONDITION',
                description: 'Counts entities that had activity before the prior period but not in the prior period period, e.g. for reactivation.',
            },
            {
                label: 'Count New',
                value: 'COUNT_FIRST_ACTIVITY_CONDITION',
                description: 'Counts entities that had their first activity in this period, e.g. for acquisition.',
            },
            {
                label: 'Sum',
                value: 'SUM_COHORT_CONDITION',
                description: 'Sums up all activity for entities in this period, e.g. for LTV.',
            },
        ];
    }, []);

    const highlightNodeIds = useMemo(() => {
        return [activeDimensionPipelineNodeId, pipelineNodeId].concat(dataWhitelist.entries.map(e => e.pipeline_node_id));
    }, [activeDimensionPipelineNodeId, pipelineNodeId, dataWhitelist])



    return <PageStructure>
        <Offcanvas placement="end" show={!!activeDimensionPipelineNodeId} onHide={() => {
            setActiveDimensionPipelineNodeId('');
        }}>
            <Offcanvas.Header closeButton>
                <Offcanvas.Title>Select Join Path</Offcanvas.Title>
            </Offcanvas.Header>
            <Offcanvas.Body>
                <Pane>
                    <PaneContent>
                        {joinPathOptions.isFetching && <LoadingCard action="Loading path options" />}
                        {!joinPathOptions.isFetching && activeDimensionJoinTree && <>
                            <div className="p-2">
                                <p>Select the join path for this metric to the selected dimension, and any filters you have set up.</p>
                                <div className="list-group">
                                    {joinPathOptionsByPipelineNodeId[activeDimensionPipelineNodeId]?.map((path, i) => {
                                        // Helper function to get relationship IDs from a join tree
                                        

                                        // Check if this path matches the current join_tree for this dimension
                                        const isSelected = !!(activeDimensionJoinTree?.join_tree && 
                                            JSON.stringify(getRelationshipIds(path).sort()) === 
                                            JSON.stringify(getRelationshipIds(activeDimensionJoinTree.join_tree).sort()));

                                        return <div className="list-group-item">
                                            <div className="d-flex center-vertically">
                                                <div className="flex-1">
                                                    <PipelineNodeJoinTreeLabel highlightNodeIds={highlightNodeIds} key={i} joinTree={path}/>
                                                </div>
                                                <div>
                                                    <button 
                                                        className={`btn btn-sm ${isSelected ? 'btn-success' : 'btn-primary'}`} 
                                                        onClick={() => {
                                                            onSelectActiveDimensionJoinPath(i);
                                                        }}
                                                        disabled={isSelected}
                                                    >
                                                        {isSelected ? 'Selected' : 'Select'}
                                                    </button>
                                                </div>
                                            </div>
                                            
                                        </div>
                                    })}
                                </div>
                                
                                {/* Custom Path Builder */}
                                <div className="mt-3">
                                    <CustomPathBuilder 
                                        startNodeId={pipelineNodeId}
                                        targetDimensionNodeId={activeDimensionPipelineNodeId}
                                        dataWhitelist={dataWhitelist}
                                        existingCustomPath={activeDimensionJoinTree?.custom_path}
                                        onSave={(customPath) => {
                                            setDimensionJoinTrees(draft => {
                                                draft.forEach(djt => {
                                                    if (djt.pipeline_node_id == activeDimensionPipelineNodeId) {
                                                        djt.custom_path = customPath;
                                                        djt.join_tree = customPath;
                                                    }
                                                });
                                            });
                                            setActiveDimensionPipelineNodeId('');
                                            setPageDirty(true);
                                        }}
                                    />
                                </div>
                                    
                            </div>
                        </>}
                        
                    </PaneContent>
                </Pane>
            </Offcanvas.Body>
        </Offcanvas>
        <PageContent>
            <PageContentHeader>
                <div className="d-flex center-vertically" style={{height: '100%'}}>
                    <div>
                        <DashboardIcon bgColor="success" icon="mdi mdi-pound"/>
                    </div>
                    <div className="flex-1">
                        <div className="small text-muted">Metric Details</div>
                        <h1 className="mb-0">{measure.data?.name}</h1>
                    </div>
                    <DraftOnly>
                        <button onClick={deleteMetric} className="btn btn-outline-danger btn-lg me-1">
                            <i className="mdi mdi-delete"></i> Delete Metric
                        </button>
                        <button onClick={copyMetric} className="btn btn-light btn-lg me-1">
                            <i className="mdi mdi-content-copy"></i> Copy Metric
                        </button>
                        <SaveButton
                            className="btn-lg"
                            disabled={!pageDirty}
                            onClick={saveMetric}
                        />
                    </DraftOnly>
                </div>
            </PageContentHeader>
            <PageContentInner hasHeader>
            <Pane>
                <PaneContent>
                        <div className="p-4">
                            <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k as string)}>
                                <Tab eventKey="basic" title="Basic Information" className="pt-3">
                                    <ReadOnly>
                                        <div className="card">
                                            <div className="card-body">
                                                {measure.isFetching && <LoadingCard action="Loading Measure Details" />}
                                                {!measure.isFetching && <>
                                                <h2>
                                                    <i className="mdi mdi-information-outline"></i> Basic Information
                                                </h2>
                                                <Form.Group className="mb-3">
                                                    <Form.Label>Metric Name</Form.Label>
                                                    <Form.Control className="w-100" value={name} onChange={(e) => {
                                                        setName(e.target.value);
                                                        setPageDirty(true);
                                                    }}/>
                                                    <Form.Text>Give your metric a name so it's easy to find.</Form.Text>
                                                </Form.Group>
                                                <Form.Group className="mb-3">
                                                    <Form.Label>Column Header</Form.Label>
                                                    <Form.Control className="w-100" value={columnHeader} onChange={(e) => {
                                                        setColumnHeader(e.target.value);
                                                        setPageDirty(true);
                                                    }}/>
                                                    <Form.Text>This is what will be used as the column header when this metric is in a report.</Form.Text>
                                                </Form.Group>
                                                <Form.Group className="mb-3">
                                                    <Form.Label>Description</Form.Label>
                                                    <Form.Control as="textarea" className="w-100" value={description} onChange={(e) => {
                                                        setDescription(e.target.value);
                                                        setPageDirty(true);
                                                    }}/>
                                                </Form.Group>
                                                <Form.Group className="mb-3">
                                                    <Form.Label>Display Format</Form.Label>
                                                    <Dropdown
                                                        options={FORMAT_OPTIONS}
                                                        selected={formatter}
                                                        onChange={(v) => {
                                                            setFormatter(v);
                                                            setPageDirty(true);
                                                        }}
                                                    />
                                                    <Form.Text>This controls how your metric is displayed in reports and other analyses.</Form.Text>
                                                </Form.Group>
                                                <Form.Group className="mb-3">
                                                    <Form.Label>Type</Form.Label>
                                                    <Dropdown
                                                        options={METRIC_TYPE_OPTIONS}
                                                        selected={metricType}
                                                        onChange={(v) => {
                                                            const newType = v as MetricType | '';
                                                            setMetricType(newType);
                                                            // Auto-set field_id to '_PLB_UUID' for COHORT type
                                                            if (newType === 'COHORT') {
                                                                setFieldId('_PLB_UUID');
                                                            }
                                                            setPageDirty(true);
                                                        }}
                                                    />
                                                    <Form.Text>Select the type of measure this metric represents.</Form.Text>
                                                </Form.Group>
                                                </>}
                                            </div>
                                        </div>
                                    </ReadOnly>
                                </Tab>
                                <Tab eventKey="source" title="Calculation" className="pt-3">
                                    {measure.isFetching && <LoadingCard action="Loading Measure Details" />}
                                    {!measure.isFetching && <>
                                    <ReadOnly>
                                        {metricType === 'CALCULATED' && <div className="card mb-3">
                                            <div className="card-body ">
                                                <h2>Calculation Details</h2>
                                                <Form.Group className="mb-3">
                                                    <Form.Label>Calculation Dependencies</Form.Label>
                                                    <MultiDropdown
                                                        options={upstreamMeasureOptions}
                                                        selected={parentMetricIds}
                                                        onChange={(newIds) => {
                                                            setPageDirty(true);
                                                            setParentMetricIds(newIds);
                                                        }}
                                                    />
                                                    <Form.Text>Which other metrics do you need to calculate this one?</Form.Text>
                                                </Form.Group>
                                                <Form.Group className="mb-3">
                                                    <div className="row">
                                                        <div className="col-8">
                                                            <Form.Label>Formula</Form.Label>
                                                            <Form.Control className="w-100 font-code" value={formula} as="textarea" onChange={(e) => {
                                                                setFormula(e.target.value);
                                                                setPageDirty(true);
                                                            }}/>
                                                            <Form.Text>You can use any Snowflake SQL here that can operate in a <code>SELECT</code> statement.</Form.Text>
                                                        </div>
                                                        <div className="col-4">
                                                            <Form.Label>Variables</Form.Label>
                                                            <div className="font-code">
                                                                {formulaVariables.join(', ')}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </Form.Group>
                                            </div>
                                        </div>}
                                        {metricType === 'BASIC' && <>
                                            <div className="card mb-3">
                                                <div className="card-body ">
                                                    <h2>Connected Entity</h2>
                                                    <Form.Group className="mb-3">
                                                        <Form.Label>Select Entity</Form.Label>
                                                        <PipelineNodeSelector
                                                            selectedId={pipelineNodeId}
                                                            onSelect={(newNode: PipelineNode | undefined) => {
                                                                setPageDirty(true);
                                                                if (!newNode) {
                                                                    setPipelineNodeId('');
                                                                } else {
                                                                    setPipelineNodeId(newNode.id as string);
                                                                }
                                                            }}
                                                            optionFilter={(pn) => {
                                                                return pn.node_type == 'DIMENSION';
                                                            }}
                                                        />
                                                    </Form.Group>
                                                    <Form.Group className="mb-3">
                                                        <Form.Label>
                                                            Select Column
                                                        </Form.Label>
                                                        <PipelineNodeColumnSelector
                                                            disabled={!pipelineNodeId}
                                                            pipelineNodeId={pipelineNodeId}
                                                            selectedId={fieldId}
                                                            onSelect={(newField) => {
                                                                setPageDirty(true);
                                                                setFieldId(newField?.id || '');
                                                            }}
                                                            includeRecordId
                                                            excludeForeignKeys
                                                        />
                                                    </Form.Group>
                                                    <Form.Group>
                                                        <Form.Label>Select Aggregator</Form.Label>
                                                        <Dropdown
                                                            disabled={!fieldId}
                                                            options={aggregationOptions}
                                                            selected={aggregator}
                                                            onChange={(v) => {
                                                                setAggregator(v);
                                                                setPageDirty(true);
                                                            }}
                                                        />
                                                        <Form.Text>How should Pliable calculate this metric?</Form.Text>
                                                    </Form.Group>
                                                </div>
                                            </div>
                                            <div className="card">
                                                <div className="card-body">
                                                    <h2 className="mb-0"><i className="mdi mdi-filter"></i> Filters (Optional)</h2>
                                                    <p className="text-muted font-13">Only include <PipelineNodeName pipelineNodeId={pipelineNodeId}/> records that match these rules. You can select columns from <PipelineNodeName pipelineNodeId={pipelineNodeId}/> as well as any connected entities.</p>
                                                    <DataWhitelistForm config={dataWhitelist} onChange={(newWhitelist) => {
                                                        setPageDirty(true);
                                                        setDataWhitelist(newWhitelist);
                                                    }} nodeIds={nodeIdsForWhitelist}/>
                                                </div>
                                            </div>
                                        </>}
                                        {metricType === 'COHORT' && <>
                                            <div className="card mb-3">
                                                <div className="card-body ">
                                                    <h2>Cohort Configuration</h2>
                                                    <Form.Group className="mb-3">
                                                        <Form.Label>Cohort Entity</Form.Label>
                                                        <PipelineNodeSelector
                                                            selectedId={pipelineNodeId}
                                                            onSelect={(newNode: PipelineNode | undefined) => {
                                                                setPageDirty(true);
                                                                if (!newNode) {
                                                                    setPipelineNodeId('');
                                                                    setFieldId('');
                                                                } else {
                                                                    setPipelineNodeId(newNode.id as string);
                                                                    setFieldId('_PLB_UUID');
                                                                }
                                                            }}
                                                            optionFilter={(pn) => {
                                                                return pn.node_type == 'DIMENSION';
                                                            }}
                                                        />
                                                        <Form.Text>The entity that represents the cohort (e.g., Users).</Form.Text>
                                                    </Form.Group>
                                                    {/* Add an option to select the aggregation from the list of cohort aggregation options */}
                                                    <Form.Group className="mb-3">
                                                        <Form.Label>Cohort Aggregation</Form.Label>
                                                        <Dropdown
                                                            options={cohortAggregationOptions}
                                                            selected={aggregator}
                                                            onChange={(v) => {
                                                                setAggregator(v);
                                                                setPageDirty(true);
                                                            }}
                                                        />
                                                        <Form.Text>How should members of the cohort be counted?</Form.Text>
                                                    </Form.Group>
                                                    
                                                    <Form.Group className="mb-3">
                                                        <Form.Label>Activity Entity</Form.Label>
                                                        <PipelineNodeSelector
                                                            selectedId={activityPipelineNodeId}
                                                            onSelect={(newNode: PipelineNode | undefined) => {
                                                                setPageDirty(true);
                                                                if (!newNode) {
                                                                    setActivityPipelineNodeId('');
                                                                    setActivityFieldId('');
                                                                } else {
                                                                    setActivityPipelineNodeId(newNode.id as string);
                                                                    setActivityFieldId('');
                                                                }
                                                            }}
                                                            optionFilter={(pn) => {
                                                                return pn.node_type == 'DIMENSION';
                                                            }}
                                                        />
                                                        <Form.Text>The entity where activities are tracked (e.g., Purchases, Events).</Form.Text>
                                                    </Form.Group>
                                                    <Form.Group className="mb-3">
                                                        <Form.Label>Activity Column</Form.Label>
                                                        <PipelineNodeColumnSelector
                                                            disabled={!activityPipelineNodeId}
                                                            pipelineNodeId={activityPipelineNodeId}
                                                            selectedId={activityFieldId}
                                                            onSelect={(newField) => {
                                                                setPageDirty(true);
                                                                setActivityFieldId(newField?.id || '');
                                                            }}
                                                        />
                                                        <Form.Text>Select the column from the activity entity to aggregate.</Form.Text>
                                                    </Form.Group>
                                                    <Form.Group className="mb-3">
                                                        <Form.Label>Activity Aggregator</Form.Label>
                                                        <Dropdown
                                                            disabled={!activityPipelineNodeId}
                                                            options={activityAggregationOptions}
                                                            selected={activityAggregation}
                                                            onChange={(v) => {
                                                                setActivityAggregation(v);
                                                                setPageDirty(true);
                                                            }}
                                                        />
                                                        <Form.Text>How should activities be aggregated for this cohort metric?</Form.Text>
                                                    </Form.Group>
                                                    
                                                </div>
                                            </div>
                                        </>}
                                    </ReadOnly>
                                    </>}
                                </Tab>
                                {metricType !== 'CALCULATED' && (
                                    <Tab eventKey="dimensions" title={`Dimension Connections (${dimensionJoinTrees.length})`} className="pt-3">
                                        <ReadOnly>
                                            <div className="card">
                                                <div className="card-body">
                                                    <h2 className="mb-0">Dimension Connections</h2>
                                                    <p className="text-muted font-13">Tell Pliable how this metric should be connected to all of your available dimensions. This controls the "joins" generated by Pliable's report builder. In many cases there is only one option, but oftentimes there are multiple paths to connect one object to another.</p>

                                                    {joinPathOptions.isFetching && <LoadingCard action="Loading Dimension Options" />}
                                                    {!joinPathOptions.isFetching && <>
                                                    <DraftOnly>
                                                        {dimensionJoinTrees.length == 0 && <Warning>
                                                            This metric is not connected to any entities! Select an entity in the "Calculation" tab and save the metric to load available dimensions.
                                                        </Warning>}
                                                    </DraftOnly>
                                                    <table className="table table-bordered table-centered table-fixed">
                                                        <thead className="bg-light">
                                                            <tr>
                                                                <th>Entity</th>
                                                                <th>Join Path</th>
                                                                <th></th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {dimensionJoinTrees.map((djt, i) => {
                                                                const possiblePaths = joinPathOptionsByPipelineNodeId[djt.pipeline_node_id];
                                                                return <tr key={i}>
                                                                    <th><PipelineNodeName pipelineNodeId={djt.pipeline_node_id}/></th>
                                                                    <td>
                                                                        {!djt.ignore && djt.join_tree && (!possiblePaths?.length || possiblePaths.length > 1) && <>
                                                                            <Badge className="clickable mb-1" bg="primary" onClick={() => {
                                                                                setActiveDimensionPipelineNodeId(djt.pipeline_node_id);
                                                                            }}>{possiblePaths?.length} Options</Badge>
                                                                        </>}
                                                                        {djt.ignore && <Badge bg="secondary">Disabled</Badge>}
                                                                        {!djt.ignore && djt.join_tree && <>
                                                                            <PipelineNodeJoinTreeLabel joinTree={djt.join_tree}
                                                                                highlightNodeIds={[djt.pipeline_node_id, pipelineNodeId, ...dataWhitelist.entries.map(e => e.pipeline_node_id)]}
                                                                            />
                                                                        </>}
                                                                        {!djt.ignore && !djt.join_tree && <>
                                                                            <Badge className="me-1 clickable" onClick={() => {
                                                                                setActiveDimensionPipelineNodeId(djt.pipeline_node_id);
                                                                            }} bg="danger">None ({possiblePaths?.length} Options)</Badge>
                                                                        </>}
                                                                    </td>
                                                                    <td className="text-end">
                                                                        {djt.ignore && <>
                                                                            <button className="btn btn-outline-primary btn-sm me-1" onClick={() => {
                                                                                enableDimension(i);
                                                                            }}>
                                                                                <i className="mdi mdi-eye-outline"></i> Enable
                                                                            </button>
                                                                        </>}
                                                                        {!djt.ignore && <>
                                                                            <button className="btn btn-outline-secondary btn-sm me-1" onClick={() => {
                                                                                disableDimension(i);
                                                                            }}>
                                                                                <i className="mdi mdi-eye-off-outline"></i> Disable
                                                                            </button>
                                                                        </>}
                                                                    </td>
                                                                </tr>
                                                            })}
                                                        </tbody>
                                                    </table>
                                                    </>}
                                                </div>
                                            </div>
                                        </ReadOnly>
                                    </Tab>
                                )}
                            </Tabs>
                            
                                                    
                                                    
                                                    
                            
                        </div>
                </PaneContent>
            </Pane>
            </PageContentInner>
            
        </PageContent>
        
    </PageStructure>
}

    export default MetricDetailsPage;